
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { Box, Button, FormLabel } from "@mui/material";
import { ContentView } from "../../components/content-view/ContentView";
import { ContentContainer } from "../../components/content-container/ContentContainer";
import { ContentSection } from "../../components/content-section/ContentSection";
import ContextualAppBar from "../../components/contextual-app-bar/ContextualAppBar";
import OptionsSideSheet from "../../components/options-side-sheet/OptionsSideSheet";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { useEffect } from "react";
import { DateRange } from "@mui/icons-material";
import { t } from "i18next";
import { CustomDurationDialog, CustomDurationDialogValues, bindCustomDurationDialog, useCustomDurationDialogState } from "../../components/custom-duration-dialog/CustomDurationDialog";
import LoadingZone from "../../components/loading-zone/LoadingZone";
import NoData from "../../components/no-data/NoData";
import { useProgressContext } from "../../contexts/progress-context/ProgressContext";
import { AppNotificationTypeEnum } from "../../contexts/app-notification-context/AppNotificationContextTypes";
import { useAppNotificationContext } from "../../contexts/app-notification-context/UseAppNotificationContext";
import _ from "lodash";
import { ApplicationMimeTypesEnum } from "../../common/enums/ApplicationMimeTypesEnum";
import UUID from "../../common/utilities/Uuid";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { ActionTypeEnum, StatusEnum } from "../../common/IExportNotificationMsg";
import AdminReportDataService from "../../services/admin-report-data-service/AdminReportDataService";
import { ActionMenuItem, BaseActionEnum } from "../../components/action-menu/ActionMenuItem";
import { RestError } from "../../services/rest-service/RestService";
import { useOrganizations } from "../../queries/organizations/UseOrganizations";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { useGroup } from "../../queries/groups/UseGroup";
import { IOrganization } from "../../services/organization-data-service/Models";
import { AgGridEvent } from "ag-grid-community";
import { ISortParameters } from "../../common/models/ISortParameters";
import useDisabledRecipientsReportState from "./DisabledRecipientsReportState";
import { useDisabledRecipientsReport } from "../../queries/admin-reports/UseDisabledRecipientsReport";
import DisabledRecipientsReportUtils from "./DisabledRecipientsReportUtils";
import { DisabledRecipientReportActionEnum } from "./DisabledRecipientsReportActions";
import NotifyMeDialog, { bindNotifyMeDialog, useNotifyMeDialogState } from "./notify-me-dialog/NotifyMeDialog";
import { DurationOptionEnum } from "../../components/custom-duration-dialog/CustomDurationUtils";
import DefaultGrid from "../../components/default-grid/DefaultGrid";


export default function DisabledRecipientsReportView() {

    const [userState] = useUserContext();
    const routeContext = useRouteContext();
    const [, setAppNotificationState] = useAppNotificationContext();
    const durationDialog = useCustomDurationDialogState();
    const notifyMeDialog = useNotifyMeDialogState();

    const [progressState, actionStarted] = useProgressContext();


    const {
        state,
        setOrganization,
        setDuration,
        setGridData,
        setActions,
        setDownloadUid,
        setGridApi,
        resetState
    } = useDisabledRecipientsReportState();


    const { data: group } = useQueryNotifications(useGroup)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number
    });

    const { data: organizations } = useQueryNotifications(useOrganizations)({
        includeAllOption: true
    });

    const organization = organizations?.find(o => o.id === (state.organizationID ?? group?.organizationID));

    const { data, status, refetch } = useQueryNotifications(useDisabledRecipientsReport)({
        contextGroupID: organization?.peerGroupID ?? routeContext.groupID as number,
        startDate: state.durationOptions.duration.startDate as Date,
        endDate: state.durationOptions.duration.endDate as Date,
    });

    const onExport = (mimeType: ApplicationMimeTypesEnum) => {

        const uid = UUID.generate();
        setDownloadUid(uid);

        actionStarted({
            uid: uid,
            objectID: routeContext.groupID as number,
            objectType: eLynxObjectTypeEnum.Group,
            title: t('views.disabledRecipientsReport'),
            notificationMsg: {
                action: ActionTypeEnum.Download,
                notificationID: uid,
                status: StatusEnum.Started,
                statusMessage: t('progress.sendingRequest'),
                additionalInfo: '',
                blobURI: '',
                percentComplete: 0
            }
        });

        try {

            const columnState = state.gridApi?.getColumnState();

            const sortItem = columnState?.find(o => !!o.sort);

            const sortParams: ISortParameters = {
                propertyName: sortItem?.colId ?? '',
                sortAscending: (sortItem?.sort ?? 'asc') === 'asc'
            };

            AdminReportDataService.exportDisabledRecipientsReport(uid,
                organization?.peerGroupID as number,
                state.durationOptions.duration.startDate as Date,
                state.durationOptions.duration.endDate as Date,
                mimeType,
                sortParams
            );

            const items = [...state.actions];
            const exportAction = items.find(o => o.action === BaseActionEnum.Export);
            if (exportAction) {
                exportAction.disabled = true;
            }
            setActions(items);
        } catch (error) {
            const restError = error as RestError;
            setAppNotificationState({
                notificationType: AppNotificationTypeEnum.Error,
                show: true,
                error: restError
            });
        }
    };

    async function onCustomDuration() {
        const result = await durationDialog.open(state.durationOptions);

        if (result.reason === "ok") {
            const values = result.values as CustomDurationDialogValues;

            setDuration(values);
        }
    }

    async function onNotifyMe() {
        await notifyMeDialog.open({
            contextGroupID: organization?.peerGroupID as number
        });
    }

    function onExecuteAction(menuItem: ActionMenuItem) {

        //const item = menuItem.action as BaseActions | DisabledRecipientReportActionEnum.NotifyMe;

        switch (menuItem.action) {
            case BaseActionEnum.Refresh: {

                const options = { ...state.durationOptions };
                if (options.option !== DurationOptionEnum.Custom) {
                    options.duration.endDate = new Date();
                    setDuration(options);
                }

                refetch();
                break;
            }

            case BaseActionEnum.Excel:
                onExport(ApplicationMimeTypesEnum.MSExcel2007);
                break;

            case DisabledRecipientReportActionEnum.NotifyMe:
                onNotifyMe();

                break;
        }
    }

    function onResetOptions() {
        resetState();
    }

    function onGridReady(event: AgGridEvent) {
        setGridApi(event.api);
    }


    function onOrganizationChanged(item: IOrganization) {
        setOrganization(item.id);
    }

    useEffect(() => {

        if (data) {

            const cols = DisabledRecipientsReportUtils.createGridColumns();
            const rows = DisabledRecipientsReportUtils.createGridRows(data);

            setGridData({
                cols,
                rows
            });

        }
    }, [
        data,
        setGridData,
    ]);


    const progress = progressState.items.find(o => o.objectID === routeContext.groupID && o.objectType === eLynxObjectTypeEnum.Group);

    useEffect(() => {

        if (progress?.uid === state.downloadUid &&
            (progress?.notificationMsg.status === StatusEnum.Completed ||
                progress?.notificationMsg.status === StatusEnum.Failed)) {
            setDownloadUid(undefined);
        }
    }, [
        progress,
        state.downloadUid,
        setDownloadUid
    ]);


    useEffect(() => {

        const items = _.cloneDeep(state.actions);

        const exportAction = items.find(o => o.action === BaseActionEnum.Export);

        if (exportAction) {
            exportAction.disabled = progress && progress.uid === state.downloadUid;
        }

        // if (!_.isEqual(state.actions, items)) {
            setActions(items);
        // }

    }, [
        state.actions,
        progress,
        state.downloadUid,
        setActions
    ]);

    useEffect(() => {
        if (group) {
            setOrganization(group.organizationID as number);
        }
    }, [
        group,
        setOrganization
    ]);

    return (
        <ContentView>
            <ContextualAppBar
                hideBreadcrumb={true}
                actions={state.actions}
                viewOptionState={state.viewOptionState}
                onExecuteAction={onExecuteAction}
                showOrganizationPicker={true}
                onOrganizationSelected={onOrganizationChanged}
                organizationID={state.organizationID}
            />

            <LoadingZone isLoading={status === 'loading'}>
                <ContentContainer>
                    <ContentSection>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                flexGrow: 1,
                                margin: '1rem',
                                overflow: "hidden"
                            }}>
                            {status === 'success' && (state.gridData?.rows.length ?? 0) === 0 &&
                                <NoData
                                    title={t('disabledRecipientsReport.nodata.title')}
                                    text={t('disabledRecipientsReport.nodata.text')}
                                />
                            }

                            {state.gridData && state.gridData.rows.length > 0 &&
                                <DefaultGrid
                                    onGridReady={onGridReady}
                                    suppressColumnVirtualisation={true}
                                    suppressMovableColumns={true}
                                    suppressMultiSort={true}
                                    rowData={state.gridData.rows}
                                    defaultColDef={{
                                        sortable: true,
                                        filter: "agMultiColumnFilter",
                                        resizable: true,
                                        // SPR 04-08-2025 - Removed autoHeight and wrapText from defaultColDef
                                        // Use multiline column type in column definitions, instead
                                        cellStyle: {
                                            'wordBreak': 'normal',
                                        }
                                    }}
                                    columnDefs={state.gridData.cols}
                                />
                            }
                        </Box>
                    </ContentSection>
                    <OptionsSideSheet onReset={onResetOptions}>
                        <FormLabel component="legend">{t('customDuration.duration')}</FormLabel>
                        <Button variant='text'
                            startIcon={<DateRange />}
                            sx={{ justifyContent: 'flex-start' }}
                            onClick={onCustomDuration}>
                            {state.durationLabel}
                        </Button>
                    </OptionsSideSheet>
                </ContentContainer>
            </LoadingZone>

            <CustomDurationDialog
                hideDefault={true}
                value={state.durationOptions}
                {...bindCustomDurationDialog(durationDialog)}
            />

            <NotifyMeDialog {...bindNotifyMeDialog(notifyMeDialog)} />
        </ContentView>
    );
}
